import React, { useState, useEffect } from 'react';
import { Box, Heading, Spinner, Center, Alert, AlertIcon, Divider, Table, Thead, Tbody, Tr, Th, Td, Badge, Text } from '@chakra-ui/react';
import Dashboard from '../components/admin/Dashboard';
import { adminService } from '../services/api';

// Safe JSON parse with fallback
const safeJsonParse = (value, fallback = {}) => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

export const AdminDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, auditRes, assessmentsRes] = await Promise.all([
          adminService.getStats(),
          adminService.getAuditLog({ limit: 20 }),
          adminService.getAssessments()
        ]);
        setStats(statsRes.data);
        setAuditLog(auditRes.data.logs);
        setAssessments(assessmentsRes.data.assessments || []);
      } catch (err) {
        setError('Failed to load admin data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  if (isLoading) {
    return (
      <Center py={20}>
        <Spinner size="xl" color="red.500" />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Heading size="lg" mt="15px" mb={6}>Admin Dashboard</Heading>

      {stats && <Dashboard stats={stats} />}

      <Divider my={8} />

      <Heading size="md" mb={4}>All Assessments</Heading>
      <Box bg="white" borderRadius="lg" boxShadow="sm" p={4} overflowX="auto" mb={8}>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>User</Th>
              <Th>Complexity</Th>
              <Th>Created</Th>
            </Tr>
          </Thead>
          <Tbody>
            {assessments.map(assessment => {
              const scores = safeJsonParse(assessment.scores, { siteScore: 0, wellScore: 0 });
              const complexity = Math.round(((scores.siteScore || 0) + (scores.wellScore || 0)) / 2 * 100);
              return (
                <Tr key={assessment.id}>
                  <Td fontWeight="medium">{assessment.name}</Td>
                  <Td>
                    <Text fontSize="sm" color="gray.600">{assessment.user_email}</Text>
                  </Td>
                  <Td>
                    <Badge colorScheme={complexity < 33 ? 'green' : complexity < 66 ? 'yellow' : 'red'}>
                      {complexity}%
                    </Badge>
                  </Td>
                  <Td fontSize="sm" color="gray.500">
                    {new Date(assessment.created_at).toLocaleDateString()}
                  </Td>
                </Tr>
              );
            })}
            {assessments.length === 0 && (
              <Tr>
                <Td colSpan={4} textAlign="center" color="gray.500">
                  No assessments yet
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      <Heading size="md" mb={4}>Recent Activity</Heading>
      <Box bg="white" borderRadius="lg" boxShadow="sm" p={4} overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Action</Th>
              <Th>User</Th>
              <Th>Entity</Th>
              <Th>Time</Th>
            </Tr>
          </Thead>
          <Tbody>
            {auditLog.map(log => (
              <Tr key={log.id}>
                <Td>
                  <Badge colorScheme="blue">{log.action}</Badge>
                </Td>
                <Td>{log.user_email || 'System'}</Td>
                <Td>{log.entity_type} #{log.entity_id}</Td>
                <Td fontSize="sm" color="gray.500">
                  {new Date(log.created_at).toLocaleString()}
                </Td>
              </Tr>
            ))}
            {auditLog.length === 0 && (
              <Tr>
                <Td colSpan={4} textAlign="center" color="gray.500">
                  No activity yet
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default AdminDashboardPage;
